import axios from 'axios';
import AdminAuthAPI from './auth.js';
import { getApiBaseUrl } from '../../utils/apiConfig';

// Create an axios instance with interceptors to handle admin token and auto-refresh
const API_BASE = getApiBaseUrl();

const adminAxios = axios.create();

adminAxios.interceptors.request.use((config) => {
  const token = AdminAuthAPI.getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  config.headers = config.headers || {};
  config.headers['Content-Type'] = 'application/json';
  return config;
});

let isRefreshing = false;
let pendingRequests = [];

async function refreshAdminToken() {
  const currentToken = AdminAuthAPI.getToken();
  if (!currentToken) throw new Error('No admin token to refresh');
  const resp = await fetch(`${API_BASE}/admin/auth/refresh`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${currentToken}` },
  });
  if (!resp.ok) throw new Error('Admin token refresh failed');
  const data = await resp.json();
  if (data && data.access_token) {
    localStorage.setItem('admin_token', data.access_token);
    // Optionally update expiry if provided
    if (data.expires_in) {
      localStorage.setItem('admin_token_expires', String(Date.now() + data.expires_in * 1000));
    }
    return data.access_token;
  }
  throw new Error('Admin token refresh response invalid');
}

adminAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const status = error?.response?.status;
    if (status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingRequests.push({ resolve, reject, originalRequest });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;
      try {
        const newToken = await refreshAdminToken();
        // Retry original request with new token
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        const result = await adminAxios(originalRequest);
        // Flush queued requests with updated token and their own original requests
        pendingRequests.forEach(({ resolve, originalRequest: queuedReq }) => {
          try {
            const req = { ...(queuedReq || {}) };
            req.headers = req.headers || {};
            req.headers['Authorization'] = `Bearer ${newToken}`;
            resolve(adminAxios(req));
          } catch (e) {
            // If something goes wrong replaying a queued request, resolve with rejection to avoid deadlocks
            resolve(Promise.reject(e));
          }
        });
        pendingRequests = [];
        return result;
      } catch (refreshErr) {
        pendingRequests.forEach(({ reject }) => reject(refreshErr));
        pendingRequests = [];
        // Clear session on hard failure
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_token_expires');
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// (dedupe) API_BASE already declared above

/**
 * Admin Sync API Client
 * Handles all sync-related operations for the admin portal
 */
class AdminSyncAPI {
  constructor() {
    this.baseURL = `${API_BASE}/admin/sync`;
  }

  /**
   * Get authentication headers
   */
  getHeaders() {
    const token = AdminAuthAPI.getToken();
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    };
  }

  /**
   * Get all tenants with their sync configuration
   */
  async getTenantsWithSyncConfig() {
    try {
      const response = await adminAxios.get(`${this.baseURL}/tenants`);
      return response.data;
    } catch (error) {
      console.error('Failed to get tenants with sync config:', error);
      throw error;
    }
  }

  /**
   * Get current sync status for all tenants
   */
  async getSyncStatus(tenantId = null) {
    try {
      const url = tenantId 
        ? `${this.baseURL}/status/${tenantId}`
        : `${this.baseURL}/status`;
      
      const response = await adminAxios.get(url);
      return response.data;
    } catch (error) {
      console.error('Failed to get sync status:', error);
      throw error;
    }
  }

  /**
   * Get sync history
   */
  async getSyncHistory(params = {}) {
    try {
      const response = await adminAxios.get(`${this.baseURL}/history`, {
        params: {
          limit: params.limit || 50,
          offset: params.offset || 0,
          tenant_id: params.tenantId,
          status: params.status,
          start_date: params.startDate,
          end_date: params.endDate
        }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get sync history:', error);
      throw error;
    }
  }

  /**
   * Start a sync job for a tenant
   */
  async startSync(tenantId, options = {}) {
    try {
      const response = await adminAxios.post(
        `${this.baseURL}/start/${tenantId}`,
        {
          sync_type: options.sync_type || 'full',
          entities: options.entities || null,
          phases: options.phases || null,
          with_dependencies: options.with_dependencies !== undefined ? options.with_dependencies : true
        },
      );
      return response.data;
    } catch (error) {
      console.error('Failed to start sync:', error);
      throw error;
    }
  }

  /**
   * Stop a running sync job
   */
  async stopSync(tenantId) {
    try {
      // Try new direct sync cancel endpoint first (works with tenant name)
      const response = await adminAxios.post(
        `${this.baseURL}/direct/${tenantId}/cancel`,
        {}
      );
      return response.data;
    } catch (error) {
      // Fallback to old endpoint if direct cancel fails (requires UUID)
      try {
        const response = await adminAxios.post(
          `${this.baseURL}/stop/${tenantId}`,
          {}
        );
        return response.data;
      } catch (fallbackError) {
        console.error('Failed to stop sync:', error);
        throw error;
      }
    }
  }

  /**
   * Retry a failed sync job
   */
  async retrySync(tenantId, jobId) {
    try {
      const response = await adminAxios.post(
        `${this.baseURL}/retry/${tenantId}/${jobId}`,
        {}
      );
      return response.data;
    } catch (error) {
      console.error('Failed to retry sync:', error);
      throw error;
    }
  }

  /**
   * Update sync configuration for a tenant
   */
  async updateSyncConfig(tenantId, config) {
    try {
      const response = await adminAxios.put(
        `${this.baseURL}/config/${tenantId}`,
        config
      );
      return response.data;
    } catch (error) {
      console.error('Failed to update sync config:', error);
      throw error;
    }
  }

  /**
   * Get detailed sync logs for a specific job
   */
  async getSyncLogs(tenantId, jobId) {
    try {
      const response = await adminAxios.get(`${this.baseURL}/logs/${tenantId}/${jobId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get sync logs:', error);
      throw error;
    }
  }

  /**
   * Get sync statistics for a tenant
   */
  async getSyncStats(tenantId, period = '7d') {
    try {
      const response = await adminAxios.get(`${this.baseURL}/stats/${tenantId}`, { params: { period } });
      return response.data;
    } catch (error) {
      console.error('Failed to get sync stats:', error);
      throw error;
    }
  }

  /**
   * Test sync connection for a tenant
   */
  async testSyncConnection(tenantId) {
    try {
      const response = await adminAxios.post(`${this.baseURL}/test/${tenantId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to test sync connection:', error);
      throw error;
    }
  }

  /**
   * Get available sync entities (general list)
   */
  async getAvailableEntities() {
    try {
      const response = await adminAxios.get(`${this.baseURL}/entities`);
      return response.data;
    } catch (error) {
      console.error('Failed to get available entities:', error);
      throw error;
    }
  }

  /**
   * Get available sync entities for a tenant
   */
  async getAvailableEntitiesForTenant(tenantId) {
    try {
      const response = await adminAxios.get(`${this.baseURL}/entities/${tenantId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get available entities for tenant:', error);
      throw error;
    }
  }

  /**
   * Schedule a sync job
   */
  async scheduleSync(tenantId, schedule) {
    try {
      const response = await adminAxios.post(`${this.baseURL}/schedule/${tenantId}`, schedule);
      return response.data;
    } catch (error) {
      console.error('Failed to schedule sync:', error);
      throw error;
    }
  }

  /**
   * Delete sync schedule
   */
  async deleteSchedule(tenantId) {
    try {
      const response = await adminAxios.delete(`${this.baseURL}/schedule/${tenantId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to delete schedule:', error);
      throw error;
    }
  }

  // ============================================================================
  // ENHANCED WORKFLOW ORCHESTRATION METHODS
  // ============================================================================

  /**
   * Get enhanced workflow status with detailed progress
   */
  async getWorkflowStatus() {
    try {
      const response = await adminAxios.get(`${this.baseURL}/workflows/status`);
      return response.data;
    } catch (error) {
      console.error('Failed to get workflow status:', error);
      throw error;
    }
  }

  /**
   * Get detailed workflow information
   */
  async getWorkflowDetails(workflowId) {
    try {
      const response = await adminAxios.get(`${this.baseURL}/workflows/${workflowId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get workflow details:', error);
      throw error;
    }
  }

  /**
   * Get enhanced workflow history with filtering
   */
  async getWorkflowHistory(params = {}) {
    try {
      const response = await adminAxios.get(`${this.baseURL}/workflows/history`, {
        params: {
          limit: params.limit || 50,
          tenant_id: params.tenantId,
          status: params.status,
          hours: params.hours || 168  // Default 7 days
        }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get workflow history:', error);
      throw error;
    }
  }

  /**
   * Start enhanced workflow using WorkflowOrchestrator
   */
  async startWorkflow(tenantId, options = {}) {
    try {
      // For client_fanout, send as JSON body instead of query params
      if (options.workflow_type === 'client_fanout') {
        const response = await adminAxios.post(
          `${this.baseURL}/workflows/start/${tenantId}`,
          {
            workflow_type: 'client_fanout',
            client_scope: options.client_scope || 'all',
            search_query: options.search_query || null,
            alpha_bucket: options.alpha_bucket || null,
            client_ids: options.client_ids || null
          }
        );
        return response.data;
      }

      // Build query parameters for other workflow types
      const params = new URLSearchParams({
        workflow_type: options.workflow_type || 'entity',
        with_dependencies: options.with_dependencies !== undefined ? options.with_dependencies.toString() : 'true'
      });

      // Add optional parameters only if they have values
      if (options.entity_name) {
        params.append('entity_name', options.entity_name);
      }
      if (options.phase_number) {
        params.append('phase_number', options.phase_number.toString());
      }

      const response = await adminAxios.post(
        `${this.baseURL}/workflows/start/${tenantId}?${params.toString()}`,
        {}
      );
      return response.data;
    } catch (error) {
      console.error('Failed to start workflow:', error);
      throw error;
    }
  }

  /**
   * Search clients by name, email, or surname for Client Fan-out scope selection
   */
  async searchClients(tenantId, query) {
    try {
      const response = await adminAxios.get(`${this.baseURL}/clients/search`, {
        params: {
          tenant_id: tenantId,
          q: query
        }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to search clients:', error);
      throw error;
    }
  }

  /**
   * Get hierarchical workflow entities with sub-task details
   */
  async getWorkflowEntities(workflowId) {
    try {
      const response = await adminAxios.get(`${this.baseURL}/workflows/${workflowId}/entities`);
      return response.data;
    } catch (error) {
      console.error('Failed to get workflow entities:', error);
      throw error;
    }
  }
}

export default new AdminSyncAPI();